import React, { useEffect, useState, createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoredLocale, setStoredLocale, getBrowserLocale, type Locale } from '../utils/language';
interface LanguageContextType {
  language: Locale;
  changeLanguage: (locale: Locale) => void;
}
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);
interface LanguageProviderProps {
  children: React.ReactNode;
  initialLocale?: Locale;
}
export function LanguageProvider({
  children,
  initialLocale
}: LanguageProviderProps) {
  const {
    i18n
  } = useTranslation();
  const [language, setLanguage] = useState<Locale>(() => {
    // Priority: stored > initial > browser > default
    return getStoredLocale() || initialLocale || getBrowserLocale();
  });
  // Initialize language on mount
  useEffect(() => {
    const stored = getStoredLocale();
    const toUse = stored || initialLocale || getBrowserLocale();
    i18n.changeLanguage(toUse);
    setLanguage(toUse);
  }, [i18n, initialLocale]);
  const changeLanguage = (locale: Locale) => {
    i18n.changeLanguage(locale);
    setStoredLocale(locale);
    setLanguage(locale);
    // Update document language attribute for accessibility
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
    }
  };
  return <LanguageContext.Provider value={{
    language,
    changeLanguage
  }} data-id="element-906">
      {children}
    </LanguageContext.Provider>;
}
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}